import {type IssueAgentTask, useIssueAgentTasks} from '../contexts/IssueAgentTasksContext'
import {getLeadingVisualIconAttributes} from '@github-ui/agent-sessions/utils/get-leading-visual-icon-attributes'
import {AgentTaskSidePanel} from './AgentTaskSidePanel'
import {
  beginCopilotUiReadiness,
  type CopilotUiReadinessView,
} from '@github-ui/copilot-browser-latency/copilot-ui-readiness'
import {IssuesLoadingSkeleton} from '@github-ui/issues-loading-skeleton'
import {useCurrentUser} from '@github-ui/current-user'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {useMediaQuery} from '@github-ui/use-media-query'
import {AnchoredOverlay, Dialog, Tooltip} from '@primer/react'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import type {IssueViewerSecondaryViewServerQueryQuery} from '@github-ui/server-persisted-queries/types/IssueViewerSecondaryViewServerQuery'

import {AgentTasksToken} from './AgentTasksToken'
import styles from './HeaderAgentTasks.module.css'
import {TaskList} from './TaskList'
import {TASK_NOUN} from '../utils/constants'
import {canViewerOpenTaskPanel} from '../utils/cli-task-visibility'
import {getSummary} from '../utils/tasks-summary'
import type {LinkedIssuePullRequest} from '../utils/types'

type IssueSecondaryNode = NonNullable<NonNullable<IssueViewerSecondaryViewServerQueryQuery['repository']>['issue']>
type LinkedPullRequestNode = NonNullable<
  NonNullable<NonNullable<IssueSecondaryNode['closedByPullRequestsReferences']>['nodes']>[number]
>

// The fields below are narrowed to the subsets HeaderMetadata's still-Relay fragment actually
// selects; StickyHeaderChips' data (the full SPQ shape) is a superset and stays assignable.
export type HeaderAgentTasksFragment = {
  readonly agentAssignments: ReadonlyArray<{readonly __typename: string}>
  readonly closedByPullRequestsReferences: {
    readonly nodes: ReadonlyArray<Pick<
      LinkedPullRequestNode,
      'id' | 'number' | 'url' | 'state' | 'databaseId'
    > | null> | null
  } | null
}

type HeaderAgentTasksProps = {
  issueData: HeaderAgentTasksFragment
  isSticky?: boolean
}

type HeaderAgentTasksInnerProps = {
  isSticky: boolean
  linkedPullRequests?: LinkedIssuePullRequest[]
}

export function HeaderAgentTasks({isSticky = false, issueData}: HeaderAgentTasksProps) {
  if (!issueData.agentAssignments.length) {
    return null
  }

  return (
    <HeaderAgentTasksInner
      linkedPullRequests={
        issueData.closedByPullRequestsReferences?.nodes
          ?.filter(node => node != null)
          .map(node => ({
            number: node.number,
            url: node.url,
            state: node.state,
            databaseId: node.databaseId,
          })) ?? []
      }
      isSticky={isSticky}
    />
  )
}

export function HeaderAgentTasksInner({linkedPullRequests, isSticky}: HeaderAgentTasksInnerProps) {
  const {data, isLoading} = useIssueAgentTasks()
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const currentUser = useCurrentUser()
  const viewerId = currentUser?.id

  const [selectedTask, setSelectedTask] = useState<IssueAgentTask | null>(null)
  const [uiReadiness, setUiReadiness] = useState<CopilotUiReadinessView>()
  const returnFocusRef = useRef<HTMLElement | null>(null)

  const tasks = useMemo(() => data?.tasks ?? [], [data?.tasks])
  const assignedAgents = useMemo(() => data?.assigned_agents ?? [], [data?.assigned_agents])
  const [isOpen, setIsOpen] = useState(false)
  // Primer's narrow viewport range
  const isNarrow = useMediaQuery('(max-width: calc(48rem - 0.02px))')

  // Copilot CLI sessions surface their lifecycle pill on the issue header to
  // anyone who can read the issue, but the raw session-log deep link itself
  // stays creator-only — see canViewerOpenTaskPanel for the rationale and
  // the planning-tracking#2952 spike.
  const allTasksGated = useMemo(
    () => tasks.length > 0 && tasks.every(task => !canViewerOpenTaskPanel(task, viewerId)),
    [tasks, viewerId],
  )

  const handleSelectTask = useCallback(
    (task: IssueAgentTask) => {
      if (!canViewerOpenTaskPanel(task, viewerId)) {
        return
      }
      const readiness = beginCopilotUiReadiness({
        surface: 'issue_panel',
        entryKind: 'embedded',
        repositoryScope: 'repository',
        expectedTaskId: task.id,
      })
      if (readiness) setUiReadiness(readiness)
      sendClickAnalyticsEvent({
        category: 'agent_task_side_panel',
        action: 'open',
        label: 'ref_cta:pill;ref_loc:issue_header',
      })
      setSelectedTask(task)
      setIsOpen(false)
    },
    [sendClickAnalyticsEvent, viewerId],
  )

  useEffect(() => {
    if (!isSticky || !isOpen) return
    const handleScroll = () => setIsOpen(false)
    // eslint-disable-next-line github/prefer-observers
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isSticky, isOpen])

  const {summaryText, summaryStatus} = useMemo(() => getSummary(tasks), [tasks])

  if (isLoading) {
    return isSticky ? (
      <span className={styles.stickySkeletonWrapper}>
        <IssuesLoadingSkeleton borderRadius="pill" height="md" width="60px" />
      </span>
    ) : (
      <IssuesLoadingSkeleton borderRadius="pill" height="xl" width="80px" />
    )
  }

  if (tasks.length === 0) {
    return null
  }

  const {icon: SummaryStatusIcon, color: summaryStatusColor} = getLeadingVisualIconAttributes(
    undefined,
    false,
    summaryStatus,
    false,
  )

  // When every task on this issue is a CLI session that the viewer didn't
  // create, the lifecycle pill still renders (so the team can see activity)
  // but the click-through to the raw session log is suppressed and a
  // tooltip explains why. See planning-tracking#2952.
  if (allTasksGated) {
    return (
      <Tooltip text="Session log is private to its creator" direction="s">
        <AgentTasksToken
          isSticky={isSticky}
          icon={SummaryStatusIcon}
          iconColor={summaryStatusColor}
          label={summaryText}
          aria-disabled
        />
      </Tooltip>
    )
  }

  const narrowView = (
    <>
      <AgentTasksToken
        icon={SummaryStatusIcon}
        iconColor={summaryStatusColor}
        label={summaryText}
        onClick={() => setIsOpen(true)}
        buttonRef={returnFocusRef}
      />
      {isOpen && (
        <Dialog
          title={`Agent ${TASK_NOUN}s`}
          onClose={() => setIsOpen(false)}
          returnFocusRef={returnFocusRef}
          position={{
            narrow: 'bottom',
            regular: 'center',
          }}
          // We use a custom renderer here to remove unwanted default padding in this view
          renderBody={() => (
            <TaskList
              tasks={tasks}
              assignedAgents={assignedAgents}
              linkedPullRequests={linkedPullRequests ?? []}
              onSelectTask={handleSelectTask}
            />
          )}
        />
      )}
    </>
  )

  const wideView = (
    <AnchoredOverlay
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      focusZoneSettings={{disabled: true}}
      overlayProps={{
        className: styles.overlay,
      }}
      anchorRef={returnFocusRef}
      renderAnchor={props => (
        <AgentTasksToken
          isSticky={isSticky}
          icon={SummaryStatusIcon}
          iconColor={summaryStatusColor}
          label={summaryText}
          {...props}
        />
      )}
    >
      <TaskList
        tasks={tasks}
        assignedAgents={assignedAgents}
        linkedPullRequests={linkedPullRequests ?? []}
        onSelectTask={handleSelectTask}
      />
    </AnchoredOverlay>
  )

  return (
    <>
      {isNarrow ? narrowView : wideView}
      {selectedTask && (
        <AgentTaskSidePanel
          uiReadiness={uiReadiness}
          taskId={selectedTask.id}
          repoId={selectedTask.repository.id}
          open
          onClose={() => setSelectedTask(null)}
          returnFocusRef={returnFocusRef}
        />
      )}
    </>
  )
}
